import { Transaction, TransactionProgress, TransactionState } from '@fry/payments/lib';

export type PaymentComponentStates = 'details' |
                                     'instructions' |
                                     'card-form' |
                                     'card-action' |
                                     'capture-form' |
                                     'outcome';

/*
Workflow for CARD PAYMENT - STRIPE
graph LR
    START((Start<br/>CARD)) --> PAYMENT
    PAYMENT -->|Completed| OUTCOME
    PAYMENT -->|Actioned| DETAILS
    PAYMENT -->|Pending| C1{Canceled?}
        C1 -->|Yes| DETAILS
        C1 -->|No| CARD_FORM[CARD_FORM<br/>Stripe]
    PAYMENT -->|Failed| CARD_FORM

    CARD_FORM -.Attempt payment.-> C6{Payment requires<br/>additional<br/>verification?}
        C6 -.Success.-> OUTCOME
        C6 -.Yes.-> CARD_ACTION
    CARD_FORM -.Cancel.-> DETAILS
    CARD_FORM -.Failed.-> CARD_FORM

    DETAILS -.Select NON CARD<br/>payment method.-> END((NON CARD<br/>workflow))
    DETAILS -.Select CARD<br/>payment method.-> CARD_FORM

    CARD_ACTION -.Failed.-> CARD_FORM
    CARD_ACTION -.Success.-> OUTCOME
    CARD_ACTION -.Cancel.->CARD_FORM    

Workflow for CARD PAYMENT - MONERIS
graph LR
    START((Start<br/>CARD)) --> PAYMENT
    PAYMENT -->|Completed| OUTCOME
    PAYMENT -->|Failed| CARD_FORM
    PAYMENT -->|Pending| C1{Canceled?}
        C1 -->|No| CARD_FORM
        C1 -->|Yes| DETAILS
    PAYMENT -->|Actioned| DETAILS

    CARD_FORM[CARD_FORM<br/>Moneis] -.Success.-> OUTCOME
    CARD_FORM -.Failed.-> OUTCOME
    CARD_FORM -.Cancel.-> DETAILS
    DETAILS -.Select NON CARD<br/>payment method.-> END((NON CARD<br/>workflow))
    DETAILS -.Select CARD<br/>payment method.-> CARD_FORM


Workflow for OFF-LINE PAYMENT
graph LR
    START((Start<br/>NOT<br/>CARD)) --> PAYMENT
    PAYMENT -->|Pending| C1{Canceled?}
        C1 -->|Yes| DETAILS
        C1 -->|No| INSTRUCTIONS
    PAYMENT -->|Actioned| OUTCOME
    PAYMENT -->|Completed| OUTCOME
    PAYMENT -->|Failed| DETAILS

    DETAILS -.Select CARD<br/>payment method.-> END((CARD<br/>workflow))
    DETAILS -.Select NON CARD<br/>payment method.-> INSTRUCTIONS
    INSTRUCTIONS -.Confirm payment.-> OUTCOME
    OUTCOME -.Change payment method.-> DETAILS
    INSTRUCTIONS -.Change payment method.-> DETAILS

*/
/**
* Payment component state to ecapsulate the logic between transitions
* 
*/
export class PaymentComponentState {

    public get state(): PaymentComponentStates {
        return this._state;
    }
    private _state: PaymentComponentStates;

    /**
     * If user who interacts with the state is owner of the trancaction
     * (eg. user who pays)
     */
    public userIsOwner: boolean;

    public userHasCapturePermission: boolean;

    public onStateChange?: (state: PaymentComponentState) => void;

    constructor(transaction: Transaction,
                isOwner: boolean = true,
                hasCapturePermission = false) {
        this.userIsOwner = isOwner;
        this.userHasCapturePermission = hasCapturePermission;

        let newState: PaymentComponentStates;
        if (isOwner) {
            if (transaction.state === TransactionState.Pending) {
                if (!transaction.isCanceled && transaction.method) {
                    newState = transaction.isCard ? 'card-form' : 'instructions';
                } else {
                    newState = 'details';
                }
            }
            if (transaction.state === TransactionState.Actioned) {
                newState = transaction.isCard ? 'details' : 'outcome';
            }
            if (transaction.state === TransactionState.Failed) {
                newState = transaction.isCard ? 'card-form' : 'details';
            }
            if (transaction.state === TransactionState.Completed) {
                newState = 'outcome';
            }
        } else {
            if (transaction.state === TransactionState.Completed) {
                newState = 'outcome';
            } else {
                newState = 'capture-form';
            }
        }

        this.authoriseTransitionTo(newState, this.userHasCapturePermission);
        this._state = newState;
        this.notifyStateChange();
    }

    public next(transaction: Transaction, progress?: TransactionProgress): string {
        let newState: PaymentComponentStates;

        switch (this.state) {
            case 'details':
                if (transaction.isCanceled) {
                    newState = 'outcome';
                } else {
                    newState = transaction.isCard ? 'card-form' : 'instructions';
                }
                break;
            case 'card-form':
                if (transaction.state === TransactionState.Failed) {
                    newState = this.state;
                } else {
                    newState = transaction.state === TransactionState.Pending &&
                               progress !== undefined
                               ? 'card-action'
                               : 'outcome';
                }
                break;
            case 'card-action':
                newState = transaction.state === TransactionState.Failed
                           ? 'card-form'
                           : 'outcome';
                break;
            case 'capture-form':
                newState = transaction.state === TransactionState.Failed
                           ? this.state
                           : 'outcome';
                break;
            case 'instructions':
            case 'outcome':
                newState = 'outcome';
                break;
        }

        this.authoriseTransitionTo(newState, this.userHasCapturePermission);
        this._state = newState;
        this.notifyStateChange();
        return this._state;
    }

    public previous(transaction: Transaction): string {
        let newState: PaymentComponentStates;

        switch (this.state) {
            case 'details':
            case 'instructions':
            case 'card-form':
            case 'capture-form':
                newState = 'details';
                break;
            case 'card-action':
                newState = 'card-form';
                break;
            case 'outcome':
                newState = transaction.isCard ? 'outcome' : 'details';
                break;
        }

        this.authoriseTransitionTo(newState, this.userHasCapturePermission);
        this._state = newState;
        this.notifyStateChange();
        return this._state;
    }

    public is(state: PaymentComponentStates) {
        return state === this._state;
    }

    public isAuthorisedToTransitionTo(state: PaymentComponentStates, hasCapturePermission: boolean) {
        return state !== 'capture-form' || hasCapturePermission;
    }

    private authoriseTransitionTo(newState: PaymentComponentStates, hasCapturePermission: boolean) {
        if (!this.isAuthorisedToTransitionTo(newState, hasCapturePermission)) {
            throw new Error($localize `You don't have permission to move to '${newState}'`);
        }
    }

    private notifyStateChange() {
        if (this.onStateChange) { this.onStateChange(this); }
    }
}
